import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '../components/ui/Button.jsx';
import { Modal } from '../components/ui/Modal.jsx';
import { useTranslation } from '../i18n/useTranslation.js';

import { ProvisioningStatusPanel } from './ProvisioningStatusPanel.jsx';
import { tenantsApi } from './platformApi.js';

/**
 * Eine Firma entfernen - acht Anlagen, ihre Rolle, ihre Konten.
 *
 * Die eine Handlung im ganzen Anbieterbereich, die sich nicht zurücknehmen
 * lässt. Deshalb hier drei Dinge, die anderswo übertrieben wären:
 *
 *   - Der Server nimmt es nur an, wenn die Firma bereits gesperrt ist. Aus
 *     einem Klick wird damit ein Vorgang mit einer Denkpause dazwischen.
 *   - Das Kürzel muss getippt werden, nicht angeklickt.
 *   - Der Befehl für die Sicherung steht dabei, zum Kopieren.
 *
 * Erzwingen kann der Server die Sicherung nicht - er kann sie nicht prüfen.
 * Ihn so zu bauen, als könnte er es, wäre die schlechtere Lösung: dann
 * verliesse man sich auf eine Zusicherung, die es nicht gibt.
 */
export function TenantDeleteModal({ open, tenant, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [typed, setTyped] = useState('');
  const [error, setError] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [finished, setFinished] = useState(null);

  const matches = typed.trim() === tenant?.slug;
  /* Der Server nimmt das Entfernen nur an, wenn die Firma GESPERRT ist.
     Das stand bisher nur im Kopfkommentar dieser Datei: man tippte das
     Kürzel, klickte, und erfuhr die Bedingung aus einer 409-Meldung. Eine
     Vorbedingung gehört vor die Handlung, nicht dahinter. */
  const gesperrt = tenant?.status === 'blocked';

  const remove = useMutation({
    mutationFn: () => tenantsApi.remove(tenant.id, tenant.slug),
    onSuccess: (data) => {
      setError(null);
      setJobId(data?.job?.id || null);
    },
    onError: (err) => setError(err?.message || t('platform.tenants.delete.failed')),
  });

  function close() {
    queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
    setTyped('');
    setError(null);
    setJobId(null);
    setFinished(null);
    onClose();
    if (finished?.status === 'done') {
      navigate('/platform/tenants');
    }
  }

  return (
    <Modal
      open={open}
      title={t('platform.tenants.delete.title', { name: tenant?.name || '' })}
      onClose={close}
      footer={
        jobId ? (
          <Button variant="primary" onClick={close} disabled={!finished}>
            {finished ? t('common.close') : t('platform.provisioning.pleaseWait')}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={close}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => remove.mutate()}
              disabled={!matches || !gesperrt || remove.isPending}
            >
              {remove.isPending ? t('platform.tenants.delete.working') : t('platform.tenants.delete.cta')}
            </Button>
          </>
        )
      }
    >
      {jobId ? (
        <ProvisioningStatusPanel jobId={jobId} onFinished={setFinished} />
      ) : (
        <>
          <div className="alert danger">{t('platform.tenants.delete.warning')}</div>

          {!gesperrt && (
            <div className="alert warning">{t('platform.tenants.delete.mustBlockFirst')}</div>
          )}

          <p className="text-sm">{t('platform.tenants.delete.backupHint')}</p>
          <pre className="pf-reset-link">node scripts/backup-tenant.js {tenant?.slug}</pre>

          <label className="field">
            <span className="field-label">
              {t('platform.tenants.delete.confirmLabel', { slug: tenant?.slug || '' })}
            </span>
            <input
              className="input mono"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              autoFocus
            />
          </label>

          {error && <div className="alert danger">{error}</div>}
        </>
      )}
    </Modal>
  );
}
